import React, { useState, useCallback, useRef, useMemo } from 'react';
import Calendar from 'react-calendar';

import { Box, Button, Dialog, Divider, Grid, Typography } from '@material-ui/core';
import {
    convertToMMDDYYYY,
    getDateBeforeEighteenYears,
    getWeekdayFirstLetter,
    validateDateOfBirth,
} from 'utils/utilities';
import PropTypes from 'prop-types';
import './Calendar.module.css';

import useDateInputStyles from './styles';

import { Input } from 'ui/FormInputs';

const CalendarModal = ({ open, setOpen, label, value, placeholder, onChange, name }) => {
    const styles = useDateInputStyles();
    const calendarRef = useRef(null);

    const getInitialValue = useCallback(() => {
        return name === 'dateOfBirth' || name === 'dob' ? getDateBeforeEighteenYears() : new Date();
    }, [name]);

    const initialDate = useMemo(() => convertToMMDDYYYY(getInitialValue()), [getInitialValue]);

    // set input state initially from value or fallback to initialDate
    const [input, setInput] = useState(value || initialDate);
    const [errMsg, setErrMsg] = useState('');

    const checkForErr = useCallback(() => {
        if (name === 'dateOfBirth' || name === 'dob') {
            return validateDateOfBirth(input);
        }
        return '';
    }, [name, input]);

    const closeModal = () => {
        setOpen(false);
    };

    // convert MM-DD-YYYY to YYY/MM/DD ( this string is supported safari)
    const handleFormatChange = (dateString) => {
        const [month, day, year] = dateString.split('-');

        const formattedString = `${year}/${month}/${day}`;

        return formattedString;
    };

    const handleInputChange = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        let formatted = '';

        if (digits.length <= 2) {
            formatted = digits;
        } else if (digits.length <= 4) {
            formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
        } else {
            formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
        }

        // Allow user-typed dash at valid positions (after MM or after DD) for better UX
        if (value.trimEnd().endsWith('-') && (digits.length === 2 || digits.length === 4)) {
            formatted += '-';
        }

        setInput(formatted);
        setErrMsg('');
    };

    const handleApply = () => {
        const error = checkForErr();
        if (!error) {
            onChange(input);
            closeModal();
        }
    };

    const handleCancel = () => {
        setInput(value || initialDate);
        closeModal();
        setErrMsg('');
    };

    const handleBlur = (e) => {
        const dateValue = e.target.value;
        if (dateValue) {
            const error = checkForErr();
            if (error) {
                setErrMsg(error);
            } else {
                try {
                    // Safari browser don't support MM-DD-YYYY format so need to use this function

                    const activeStartDate = new Date(handleFormatChange(dateValue));

                    const newDate = new Date(dateValue);

                    if (isNaN(newDate.getTime())) {
                        throw new Error('Error handleBlur func - newDate');
                    }

                    if (isNaN(activeStartDate.getTime())) {
                        throw new Error('Error handleBlur func - activeStartDate');
                    }

                    setInput(convertToMMDDYYYY(newDate));

                    // Safari browser gives error when passing date string so need to pass formatted string here
                    calendarRef.current.setActiveStartDate(activeStartDate);

                    setErrMsg(checkForErr());
                } catch (error) {}
            }
        }
    };

    const onDateChange = (newDate) => {
        const formattedDate = convertToMMDDYYYY(newDate);
        setInput(formattedDate);
        calendarRef.current.setActiveStartDate(newDate);
        setErrMsg(checkForErr());
    };

    const calculateMaxDate = useMemo(() => {
        if (name === 'dateOfBirth' || name === 'dob') {
            const minDate = new Date();
            minDate.setFullYear(minDate.getFullYear() - 18);
            return minDate;
        }
        return null;
    }, [name]);

    const getDefaultViewDate = useMemo(() => {
        if (value) {
            try {
                // Safari browser don't support MM-DD-YYYY or MM/DD/YYYY format so need to use this function

                const newDate = new Date(handleFormatChange(value));

                if (isNaN(newDate.getTime())) {
                    throw new Error('Error: getDefaultDate func');
                }

                return newDate;
            } catch (error) {}
        } else if (name === 'dateOfBirth' || name === 'dob') {
            const minDate = new Date();
            minDate.setFullYear(minDate.getFullYear() - 18);
            return minDate;
        }
        return new Date();
    }, [name, value]);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    closeModal();
                }
            }}
            // disablePortal renders this Dialog inside the parent UserDetails bootstrap
            // Modal's DOM. That modal's enforceFocus only allows focus on nodes it
            // contains; a portaled Dialog lives at <body> (outside it) and would have
            // its input focus repeatedly stolen, so keep it in-tree here.
            disablePortal
            disableEscapeKeyDown
            classes={{ paper: styles.dialogPaper }}
        >
            <Box className={styles.modalWrapper}>
                <Grid className={styles.inputFieldWrapper}>
                    <Box>
                        <Typography className={styles.inputLabel}>{label}</Typography>
                        <Typography className={styles.inputHelperText}>
                            You can type the date or pick from the calendar below
                        </Typography>
                    </Box>
                    <Input
                        variant="outlined"
                        value={input}
                        name="input"
                        onBlur={handleBlur}
                        onChange={(e) => handleInputChange(e.target.value)}
                        className={styles.inputField}
                        placeholder={placeholder}
                        onKeyDown={(e) => e.key === 'Enter' && !errMsg && handleBlur(e)}
                        error={Boolean(errMsg)}
                    />

                    {errMsg && <Typography className={styles.inputErr}>{errMsg}</Typography>}
                </Grid>
                <Divider className={styles.divider} />
                <Box className={styles.calendarWrapper}>
                    <Calendar
                        ref={calendarRef}
                        className={styles.calendar}
                        value={
                            input?.length === 10
                                ? new Date(handleFormatChange(input))
                                : getDefaultViewDate
                        }
                        onChange={onDateChange}
                        formatShortWeekday={getWeekdayFirstLetter}
                        showNavigation={true}
                        prev2Label={null}
                        next2Label={null}
                        defaultActiveStartDate={getDefaultViewDate}
                        maxDate={calculateMaxDate}
                    />
                </Box>
                <Divider className={styles.divider} />
                <Box className={styles.footer}>
                    <Button className={styles.cancelBtn} onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        className={styles.applyBtn}
                        onClick={handleApply}
                        disabled={Boolean(errMsg) || !input}
                    >
                        Apply
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

CalendarModal.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func.isRequired,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    name: PropTypes.string.isRequired,
};

export default CalendarModal;
